import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CustomButton from "components/buttons/CustomButton";
import { useAlert } from "hooks/useAlert";
import SpinningCircle from "components/spinners/SpinningCircle";

const MassAssetImportUploader = ({ onFileUpload, requiredHeaders }) => {
  const { showError, showSuccess } = useAlert();
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState("");

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        if (
          file.type !==
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
          showError("Το αρχείο πρέπει να είναι τύπου .xlsx");
          return;
        }

        setIsUploading(true);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const headers = json[0];
          const missingHeaders = requiredHeaders?.filter(
            (header) => !headers?.includes(header)
          );

          if (missingHeaders?.length > 0) {
            showError(
              "Οι στήλες του excel που ανεβάσατε δεν μοιάζουν με αυτές του προτύπου"
            );
            setIsUploading(false);
            setFileName("");
            return;
          }

          const rowData = XLSX.utils.sheet_to_json(worksheet);

          onFileUpload(rowData);
          setIsUploading(false);
          showSuccess("Το αρχείο ανέβηκε με επιτυχία");
        };
        reader.readAsArrayBuffer(file);
      }
    },
    [onFileUpload, requiredHeaders, showError, showSuccess]
  );

  const handleClearFile = () => {
    setFileName("");
    setIsUploading(false);
    onFileUpload(null);
    showSuccess("Το αρχείο αφαιρέθηκε με επιτυχία");
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: ".xlsx",
    onDrop,
    multiple: false,
    disabled: !!fileName,
  });

  return (
    <Box
      {...(!fileName ? getRootProps() : {})}
      sx={{
        border: "2px dashed #ddd",
        padding: 2,
        borderRadius: 2,
        textAlign: "center",
        position: "relative",
        cursor: fileName ? "default" : "pointer",
        backgroundColor: fileName ? "#f0f8ff" : "#fff",
        transition: "background-color 0.3s",
      }}
    >
      <input {...getInputProps()} />
      <Typography variant="body1">
        {fileName
          ? `Αρχείο: ${fileName}`
          : "Σύρετε το αρχείο σας εδώ ή κάντε κλικ για να επιλέξετε αρχείο"}
      </Typography>
      {isUploading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            height: "100%",
          }}
        >
          <SpinningCircle />
        </Box>
      )}
      {fileName && !isUploading && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <CustomButton
            title="Διαγραφή"
            onClick={handleClearFile}
            orientation="row"
            fontSize={14}
            fontWeight={600}
            sx={{
              minWidth: 120,
              maxHeight: 20,
              paddingY: 3,
              borderRadius: 3,
            }}
          />
        </Box>
      )}

      {!isUploading && !fileName && (
        <CustomButton
          title="Επιλογή Αρχείου"
          orientation="row"
          fontSize={14}
          fontWeight={600}
          sx={{
            marginTop: 2,
            minWidth: 120,
            maxHeight: 20,
            paddingY: 3,
            borderRadius: 3,
          }}
        />
      )}
    </Box>
  );
};

MassAssetImportUploader.propTypes = {
  onFileUpload: PropTypes.func.isRequired,
  requiredHeaders: PropTypes.arrayOf(PropTypes.string),
};

export default MassAssetImportUploader;
